import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Loading from "components/Loading";
import Modal from "components/Modal";
import useCancelRequestEstimate, { CancelReasonCode } from "hooks/api/useCancelRequestEstimate";
import useFindContractById from "hooks/api/useFindContractById";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import CancelRequestReasonForm from "templates/CancelRequestReasonForm";
import { getCancelRequestRequestedAt } from "utils/cancelRequestSession";
import { isBrowser, isTablet } from "utils/deviceDetect";
import { isSameOrAfterToday } from "utils/dateUtils";

const UsageDetailPresenter = lazy(() =>
  isBrowser
    ? import("pages/UsageDetail/pc/UsageDetailPresenter")
    : isTablet
      ? import("pages/UsageDetail/tb/UsageDetailPresenter")
      : import("pages/UsageDetail/sp/UsageDetailPresenter")
);

const UsageDetailContainer = () => {
  const parameter = useParams();
  const navigate = useNavigate();
  const { contract, error: contractDetailError } = useFindContractById(parameter.id ?? "");
  const { cancelRequestEstimate } = useCancelRequestEstimate();
  const [cancelRequestedQuoteIds, setCancelRequestedQuoteIds] = useState<string[]>([]);
  const [cancelRequestModalOpen, setCancelRequestModalOpen] = useState(false);
  const [isCancelRequestLoading, setIsCancelRequestLoading] = useState(false);

  const { showErrorModal } = useErrorModalStatusContext();
  useEffect(() => {
    if (contractDetailError) {
      showErrorModal({ httpMethod: "get", onPostClose: () => navigate(-1) });
    }
  }, [contractDetailError, navigate, showErrorModal]);

  const showCancelRequestLink =
    contract != null &&
    Boolean(contract.opportunityId) &&
    Boolean(contract.quoteId) &&
    !isSameOrAfterToday(contract.usageEndDate);
  const requestedAt = contract?.quoteId ? getCancelRequestRequestedAt(contract.quoteId) : undefined;
  const isCancelRequested =
    contract?.quoteId != null && (cancelRequestedQuoteIds.includes(contract.quoteId) || requestedAt != null);

  const onOpenCancelRequest = useCallback(() => {
    if (contract?.opportunityId == null || contract?.quoteId == null) return;
    if (isBrowser) {
      navigate(`/order/${contract.opportunityId}/estimate/${contract.quoteId}/cancel-request`);
      return;
    }
    setCancelRequestModalOpen(true);
  }, [contract?.opportunityId, contract?.quoteId, navigate]);

  const handleCancelRequestSubmit = useCallback(
    async (cancelReason: string) => {
      if (contract?.opportunityId == null || contract?.quoteId == null) return;
      setIsCancelRequestLoading(true);
      try {
        await cancelRequestEstimate(contract.opportunityId, contract.quoteId, {
          cancelReasonCode: CancelReasonCode.OTHER,
          cancelReasonDetail: cancelReason,
        });
        setCancelRequestedQuoteIds((prev: string[]) => [...prev, contract.quoteId as string]);
        setCancelRequestModalOpen(false);
      } catch {
        showErrorModal({ httpMethod: "post" });
      } finally {
        setIsCancelRequestLoading(false);
      }
    },
    [contract?.opportunityId, contract?.quoteId, cancelRequestEstimate, showErrorModal]
  );

  return (
    <>
      <Suspense fallback={<Loading />}>
        <UsageDetailPresenter
          contract={contract}
          onBack={() => navigate(-1)}
          showCancelRequestLink={showCancelRequestLink}
          isCancelRequested={isCancelRequested}
          cancelRequestedAt={requestedAt}
          onOpenCancelRequestModal={onOpenCancelRequest}
        />
      </Suspense>
      {!isBrowser && (
        <Modal
          isVisible={cancelRequestModalOpen}
          onClose={() => setCancelRequestModalOpen(false)}
          height="auto"
          width="auto"
        >
          <CancelRequestReasonForm
            onSubmit={handleCancelRequestSubmit}
            onCancel={() => setCancelRequestModalOpen(false)}
            isLoading={isCancelRequestLoading}
          />
        </Modal>
      )}
    </>
  );
};

export default UsageDetailContainer;
